import { Logo } from "@koaform/ui";
import { useEffect, useState } from "react";

import { AuthForm } from "../auth/AuthForm.js";

type Step =
  { name: "auth" } | { name: "publishing" } | { name: "done"; link: string };

/**
 * The signup gate and the published link, in one dialog.
 *
 * Everything happens in place — a publish flow must never leave the page the
 * draft lives on. Auth itself is the shared AuthForm, the same component the
 * login screen uses, so the two entrances to an account cannot drift.
 */
export const PublishDialog = ({
  title,
  slug,
  rendererUrl,
  onPublish,
  onClose,
}: {
  title: string;
  slug: string | null;
  rendererUrl: string;
  /** Runs after sign-in succeeds. Resolves with the slug once live. */
  onPublish: () => Promise<string | null>;
  onClose: () => void;
}) => {
  const [step, setStep] = useState<Step>(
    slug ? { name: "done", link: `${rendererUrl}/r/${slug}` } : { name: "auth" }, // prettier-ignore
  );
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Follow the slug once it arrives.
   *
   * The initial step is computed in a useState initializer, which runs once —
   * and the dialog is opened by the Publish button BEFORE the publish call
   * resolves, so an already-signed-in user mounted it with slug still null
   * and sat on "Create an account to publish" while their form went live
   * behind the overlay.
   */
  useEffect(() => {
    if (slug) setStep({ name: "done", link: `${rendererUrl}/r/${slug}` });
  }, [slug, rendererUrl]);

  /** Runs once the shared AuthForm confirms the sign-in. */
  const publishAfterAuth = async () => {
    setError(null);
    // Signing in claimed the anonymous draft, so this publishes the very form
    // behind the dialog.
    setStep({ name: "publishing" });
    const published = await onPublish();
    if (!published) {
      setStep({ name: "auth" });
      setError("Could not publish that form. Try again.");
      return;
    }
    setStep({ name: "done", link: `${rendererUrl}/r/${published}` });
  };

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard needs a secure context; the field is selectable regardless.
    }
  };

  const secondary =
    "w-full rounded-[11px] border border-line-bold bg-surface p-[13px] text-[15px] font-semibold text-ink hover:border-primary-600";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={step.name === "done" ? "Your form is live" : "Publish"}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade absolute inset-0 z-50 flex items-center justify-center bg-ink/45 p-6 backdrop-blur-[3px]"
    >
      <div className="animate-rise w-[440px] max-w-full rounded-[20px] bg-surface p-[34px] pb-7 shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]">
        {step.name === "done" ? (
          <>
            <div className="mb-[18px] flex h-[46px] w-[46px] items-center justify-center rounded-[14px] bg-primary-100 text-[22px] font-bold text-primary-600">
              ✓
            </div>
            <h2 className="m-0 mb-2 text-[25px] leading-[1.15] font-extrabold tracking-[-0.03em]">
              Your form is live
            </h2>
            <p className="m-0 mb-5 text-[15px] leading-[1.55] text-muted">
              Share the link anywhere. Responses land in your dashboard as they
              come in.
            </p>

            <div className="mb-5 flex gap-2">
              <input
                readOnly
                value={step.link}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-[11px] border border-line-input bg-subtle p-[13px] font-mono text-[13px] text-body"
              />
              <button
                type="button"
                onClick={() => void copy(step.link)}
                className="flex-none rounded-[11px] border border-line-bold bg-surface px-4 text-sm font-semibold text-ink hover:border-primary-600"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <a
              href={step.link}
              className="mb-2.5 block rounded-[11px] bg-primary-600 p-3.5 text-center text-[15px] font-bold text-white hover:bg-primary-700 hover:text-white"
            >
              Open the live form
            </a>
            <button type="button" onClick={onClose} className={secondary}>
              Keep editing
            </button>
          </>
        ) : (
          <>
            <div className="mb-[18px] flex items-start justify-between">
              <Logo size={30} />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-[30px] w-[30px] rounded-lg border-0 bg-line text-[15px] text-muted hover:bg-line-input"
              >
                ×
              </button>
            </div>

            {step.name === "publishing" ? (
              <>
                <h2 className="m-0 mb-2 text-[25px] leading-[1.15] font-extrabold tracking-[-0.03em]">
                  Publishing…
                </h2>
                <p className="m-0 text-[15px] leading-[1.55] text-muted">
                  Putting <strong className="text-ink">{title}</strong> live.
                </p>
              </>
            ) : (
              <>
                <h2 className="m-0 mb-2 text-[25px] leading-[1.15] font-extrabold tracking-[-0.03em]">
                  Create an account to publish
                </h2>
                <p className="m-0 mb-[22px] text-[15px] leading-[1.55] text-muted">
                  Your draft <strong className="text-ink">{title}</strong> is
                  saved. It'll be waiting the second you're in.
                </p>

                <AuthForm
                  onSignedIn={publishAfterAuth}
                  submitLabel="Sign up and publish"
                />

                {error ? (
                  <p
                    role="alert"
                    className="mt-3 mb-0 text-[13.5px] text-danger"
                  >
                    {error}
                  </p>
                ) : null}

                <p className="mt-4 mb-0 text-center text-[12.5px] leading-[1.5] text-faint">
                  Free forever — unlimited forms and responses. No card, ever.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
